import { onMounted, ref, watch, computed } from 'vue'
import {
  useStoreGoogleOAuthCredentialForPlannerAccountMutation,
  useRevokeGoogleOAuthTokenForPlannerAccountMutation,
} from './useGoogleAuthorization.generated'
import { OAuthProvider } from '@/types/schema.generated'

const addGoogleApiLibTagIfNeeded = (() => {
  // eslint-disable-next-line fp/no-let
  let isTagAdded = false
  return () => {
    if (isTagAdded) return
    isTagAdded = true
    const script = document.createElement('script')
    script.setAttribute('src', 'https://accounts.google.com/gsi/client')
    script.setAttribute('async', 'true')
    script.setAttribute('defer', 'true')
    document.head.appendChild(script)
  }
})()

const useOnGoogleLibraryLoad = () => {
  const callbacks = ref<(() => void)[]>([])
  const isLibLoaded = ref(false)

  return {
    addCallback: (cb: () => void) => {
      if (isLibLoaded.value) {
        cb()
      } else {
        callbacks.value = [...callbacks.value, cb]
      }
    },
    call: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).onGoogleLibraryLoad = () => {
        isLibLoaded.value = true
        callbacks.value.forEach(cb => cb())
      }
      addGoogleApiLibTagIfNeeded()
    },
  }
}

const onGoogleLibraryLoad = useOnGoogleLibraryLoad()

export const useGoogleAuthorizationForPlannerAccount = (() => {
  return (scopes: string[]) => {
    onMounted(() => {
      onGoogleLibraryLoad.call()
    })

    const {
      mutate,
      onError,
      onDone,
      loading: isMutationRunning,
    } = useStoreGoogleOAuthCredentialForPlannerAccountMutation({})
    onError(err => console.warn(err))
    const sendAuthCode = (code: string) =>
      mutate({
        code: code,
        redirectUri: document.location.origin,
      })

    const isAuthorizationRunning = ref(false)
    const client = ref<google.accounts.oauth2.CodeClient>()
    const isClientReady = ref(false)
    const initClient = () => {
      client.value = google.accounts.oauth2.initCodeClient({
        client_id: import.meta.env.VITE_GOOGLE_API_CLIENT_ID,
        scope: scopes.join(' '),
        ux_mode: 'popup',
        callback: response => {
          isAuthorizationRunning.value = false
          sendAuthCode(response.code)
        },
        error_callback: _ => (isAuthorizationRunning.value = false),
      })
      isClientReady.value = true
    }

    onGoogleLibraryLoad.addCallback(() => {
      initClient()
    })

    const requestAndSendCode = () => {
      isAuthorizationRunning.value = true
      // isClientReady は一度の hook 呼び出しで一度しか変更されないので unwatch しなくても安全
      // immediate true だと callback の中で unwatch するのが大変なのでそのままにしてる
      watch(
        isClientReady,
        (isClientReady: boolean) => {
          if (!isClientReady) return
          client.value?.requestCode()
        },
        { immediate: true }
      )
    }

    const loading = computed(
      () => isAuthorizationRunning.value || isMutationRunning.value
    )

    const revokeOAuthTokenMutation =
      useRevokeGoogleOAuthTokenForPlannerAccountMutation()

    const revoke = async () =>
      await revokeOAuthTokenMutation.mutate({
        input: {
          provider: OAuthProvider.Google,
        },
      })

    return { requestAndSendCode, onDone, loading, revoke }
  }
})()
